<script setup>

import {useGetData} from "@/components/common/Composables/useGetData";
import UseGEStore from "@/views/GraphExplorer/Composables/UseGEStore";
import {computed, watch} from "vue";
import {DataState} from "@/components/common/types/data";
import FancyCard from "@/components/common/Cards/FancyCard.vue";
import NameValueFlex from "@/components/common/NameValueFlex.vue";
import PrimarySelector from "@/components/common/PrimarySelector.vue";
import AddressCategoryIcon from "@/components/address/AddressCategoryIcon.vue";
import Copyable from "@/components/common/Copyable.vue";
import store from "@/store";

const {GESelectedElement, GETransactions} = UseGEStore()
const addressId = computed(() => GESelectedElement.value.data?.typeSpecific.address)

const {data, dataState, getData, error} = useGetData('getAddressInfo', {
    params: {
        address: addressId.value,
        currencyUnit: "BTC"
    }
}, false)

const loading = computed(() => dataState.value === DataState.Initial || dataState.value === DataState.Loading)
const addressData = computed(() => data.value?.data)
const bitcoinCurrency = computed(() => store.getters.currencyByUnit("BTC"))

const computedOwners = computed(() => {
    if (loading.value || !addressData.value)
        return []
    return addressData.value.owners.map(owner => {
        return {...owner, isPrimary: owner.id === addressData.value.owner?.id}
    });
})

const headersOwners = [
    {text: 'Name', value: 'name', align: 'center'},
    {text: 'Primary', value: 'isPrimary', align: 'center'},
]

watch(addressId, async () => {
    if (!addressId.value)
        return

    getData({
        params: {
            address: addressId.value,
            currencyUnit: "BTC"
        },
    }, true)
}, {immediate: true})

</script>

<template>
    <v-row>
        <v-col>
            <FancyCard>
                <template #header>
                    <div class="d-flex align-center">
                        <v-icon class="ml-1" left style="height: 24px; width: 24px">mdi-tag-text-outline
                        </v-icon>
                        <span class="font-weight-bold">BTC</span>&nbsp;Address
                    </div>
                </template>
                <template #body>
                    <NameValueFlex>
                        <template #name>
                            Name:
                        </template>
                        <template #value>
                            <Copyable :on-copy="addressId">
                                <template #text="{on, attr}">
                                    <div
                                        class="fontMonospace"
                                        style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                        v-bind="attr"
                                        v-on="on"
                                    >
                                        {{ addressId | stringMiddleShortener(50) }}
                                    </div>
                                </template>
                            </Copyable>
                        </template>
                    </nameValueFlex>
                    <NameValueFlex :loading="loading" loading-width="150">
                        <template #name>
                            Label:
                        </template>
                        <template #value>
                            <v-chip
                                v-if="addressData.identity"
                                color="BTC"
                                label
                                outlined
                            >
                        <span class="fontMonospace" style="vertical-align: middle"> {{
                                addressData.identity.label !== "" ? addressData.identity.label : "Undefined"
                            }}</span>
                            </v-chip>
                            <span v-else class="font-italic">
                        None
                    </span>
                        </template>
                    </nameValueFlex>
                    <div v-if="!loading && addressData" class="mt-1">
                        <AddressCategoryIcon
                            v-for="cat in addressData.categories"
                            v-bind:key="cat.id"
                            :iconItem="cat"
                            class="mr-2 mb-1"
                            medium
                        />
                    </div>
                </template>
            </FancyCard>
        </v-col>
        <v-col>
            <FancyCard>
                <template #header>
                    <div class="d-flex align-center">
                        <img alt="Owner" src="/img/ico/owner-icon.svg"
                             style="filter: opacity(0.6); width: 24px; margin-right: 8px"/>
                        <span>Owners</span>
                    </div>
                </template>
                <template #body>
                    <div v-if="loading">
                        <v-skeleton-loader
                            height="173"
                            type="image"
                        ></v-skeleton-loader>
                    </div>
                    <div v-else>
                        <v-data-table
                            v-if="computedOwners.length > 0"
                            :headers="headersOwners"
                            :items="computedOwners"
                            :items-per-page="-1"
                            class="border"
                            dense
                            hide-default-footer
                        >
                            <template slot="item" slot-scope="props">
                                <tr>
                                    <td class="text-overflow fontMonospace">
                                        {{ props.item.name }}
                                    </td>
                                    <td class="text-overflow">
                                        <PrimarySelector :currency="bitcoinCurrency"
                                                         :isPrimary="props.item.isPrimary"
                                                         :item="props.item"
                                        />
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                        <div v-else class="title font-weight-light text-center">
                            No owners assigned.
                        </div>
                    </div>
                </template>
            </FancyCard>
        </v-col>
    </v-row>

</template>

<style scoped>

</style>